import { Link } from "@tanstack/react-router";
import type { Route, RouteTrack } from "@/app/queries";
import { ProgressRing } from "@/ui/Progress";
import { trackRingColor, trackStateLabel } from "@/ui/route";
import { mainTracks, parallelTrack } from "./derive";

function isActive(t: RouteTrack) {
  return t.steps.some((s) => s.state === "active");
}
function isClosed(t: RouteTrack) {
  return !isActive(t) && t.done === 0;
}

function TrackCard({ track, dashed = false, state }: { track: RouteTrack; dashed?: boolean; state: string }) {
  const active = isActive(track);
  const closed = !dashed && isClosed(track);
  const border = dashed ? "border-dashed border-bd2" : active ? "border-bd2" : "border-bd";
  const bg = active && !dashed ? "bg-accsoft" : "bg-card";
  return (
    <Link
      to="/track/$slug"
      params={{ slug: track.slug }}
      className={`grid grid-cols-[44px_minmax(0,1fr)] items-center gap-3 rounded-md border p-2.5 no-underline ${border} ${bg}`}
      data-testid={`track-${track.slug}`}
    >
      <ProgressRing done={track.done} total={track.total} size={44} stroke={4} color={trackRingColor(track)} />
      <span className="min-w-0">
        <span className={`block overflow-hidden text-ellipsis whitespace-nowrap text-[13px] ${closed ? "text-mut" : "text-ink"}`}>{track.name}</span>
        <span className={`mt-[3px] block font-mono text-[11px] ${active && !dashed ? "text-ink2" : "text-mut"}`}>{state}</span>
      </span>
    </Link>
  );
}

/** Рейл «треки» (S1 · 1a): кольца 44/4, «12 / 36 шагов», параллельный пунктиром. */
export function TracksRail({ route }: { route: Route }) {
  const main = mainTracks(route.tracks);
  const para = parallelTrack(route.tracks);
  const activeName = route.active_track?.name;
  return (
    <aside className="flex flex-col gap-4" aria-labelledby="tracks-title">
      <div className="flex items-baseline justify-between">
        <h2 id="tracks-title" className="m-0 font-mono text-[13px] font-semibold uppercase tracking-[.04em] text-mut">
          треки
        </h2>
        <span className="font-mono text-[11px] text-mut" data-testid="steps-total">
          {route.done} / {route.total} шагов
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {main.map((t) => (
          <TrackCard key={t.id} track={t} state={trackStateLabel(t)} />
        ))}
      </div>
      {para && (
        <div className="mt-1 border-t border-dashed border-bd2 pt-3.5">
          <div className="mb-2.5 font-mono text-[10.5px] uppercase tracking-[.06em] text-accdim">параллельный трек</div>
          <TrackCard track={para} dashed state={activeName && activeName !== para.name ? `идёт вместе с треком «${activeName}»` : trackStateLabel(para)} />
          <p className="m-0 mt-2.5 text-[12px] leading-[1.45] text-mut text-pretty">Примерно один алгоритмический шаг на один шаг основного маршрута.</p>
        </div>
      )}
    </aside>
  );
}
